import React from 'react';
import CodeBlock from '../../components/common/CodeBlock';
import AuthorInfo from '../../components/common/AuthorInfo';
import { SectionHeading, SubHeading, Para, Mark, Code, Callout, Output, Pill, QA } from './articleKit';

type Author = { name: string; fullName: string; avatar?: string };

export const JSTemplateLiterals: React.FC<{ author: Author }> = ({ author }) => (
 <div className="max-w-none">
  {/* Hero header */}
  <div className="mb-10 pb-6 border-b border-gray-200 dark:border-gray-700">
   <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white mb-4">Template Literals</h1>
   <p className="text-lg leading-8 text-gray-600 dark:text-gray-400 mb-5">
    Backtick strings with superpowers — interpolation, embedded expressions, multi-line text, and tagged templates that replaced messy <code>+</code> concatenation.
   </p>
   <AuthorInfo name={author.name} fullName={author.fullName} avatar={author.avatar} lastUpdated="June 2026" readTime="12 min read" />
  </div>

  {/* 1. Real Problem */}
  <section className="mb-12">
   <SectionHeading n={1}>The Real Problem</SectionHeading>
   <Para>Before ES6, building dynamic strings meant gluing pieces together with <Code>+</Code>:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const email =\n  "Hello " + customer +\n  ", Your order #" + orderId +\n  " worth ₹" + amount +\n  " has been confirmed.";` }]} defaultLanguage="javascript" id="tl-problem" />
   <Para>For emails, HTML, queries, logs, and reports this is hard to read, error-prone, and full of stray <Code>+</Code> operators. Template literals fix it.</Para>
  </section>

  {/* 2. Definition */}
  <section className="mb-12">
   <SectionHeading n={2}>Definition</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4 mb-4">
    <p className="text-[15px] leading-7 text-gray-800 dark:text-gray-200"><Mark>Template literals</Mark> are ES6 strings written with <Mark>backticks</Mark> that support interpolation, multi-line strings, embedded expressions, and tagged templates.</p>
   </div>
   <CodeBlock snippets={[{ language: 'javascript', code: `// before\n"Hello " + name\n\n// after\n\`Hello \${name}\`` }]} defaultLanguage="javascript" id="tl-definition" />
  </section>

  {/* 3. Why */}
  <section className="mb-12">
   <SectionHeading n={3}>Why They Exist</SectionHeading>
   <div className="flex flex-wrap gap-2">{['Cleaner code', 'Better readability', 'Less boilerplate', 'Easier maintenance'].map((b) => <Pill key={b}>{b}</Pill>)}</div>
  </section>

  {/* 4. Core idea */}
  <section className="mb-12">
   <SectionHeading n={4}>Core Idea — Strings With Superpowers</SectionHeading>
   <Para>A plain string only stores text. A template literal can <Mark>execute logic while building text</Mark> — dynamic variables, expressions, multi-line content, and custom processing.</Para>
  </section>

  {/* 5. Basic syntax */}
  <section className="mb-12">
   <SectionHeading n={5}>Basic Syntax</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `const name = "Sachin";\n\nconst a = "Hello " + name;  // traditional\nconst b = \`Hello \${name}\`;   // template literal -> "Hello Sachin"` }]} defaultLanguage="javascript" id="tl-basic" />
   <Callout tone="warning">Template literals use <Mark>backticks</Mark> (<Code>`</Code>), not single or double quotes.</Callout>
  </section>

  {/* 6. Interpolation */}
  <section className="mb-12">
   <SectionHeading n={6}>String Interpolation</SectionHeading>
   <Para>Inject values directly into the string with <Code>{`\${ }`}</Code>:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const product = "Laptop";\nconst price = 80000;\n\nconst message = \`Product: \${product} | Price: ₹\${price}\`;\n// "Product: Laptop | Price: ₹80000"` }]} defaultLanguage="javascript" id="tl-interp" />
  </section>

  {/* 7. Expressions */}
  <section className="mb-12">
   <SectionHeading n={7}>Expressions Inside Templates</SectionHeading>
   <Para>Anything that returns a value can go inside <Code>{`\${ }`}</Code> — arithmetic, method calls, ternaries:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const price = 1000, tax = 180;\n\n\`Total: ₹\${price + tax}\`        // ₹1180\n\`Max: \${Math.max(5, 10)}\`        // Max: 10\n\`Admin? \${isAdmin ? "Yes" : "No"}\`` }]} defaultLanguage="javascript" id="tl-expr" />
  </section>

  {/* 8. Function calls */}
  <section className="mb-12">
   <SectionHeading n={8}>Function Calls Inside Templates</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `function greet(name) {\n  return \`Hello \${name}\`;\n}\n\nconst message = \`\${greet("Sachin")}\`; // "Hello Sachin"` }]} defaultLanguage="javascript" id="tl-func" />
  </section>

  {/* 9. Multi-line */}
  <section className="mb-12">
   <SectionHeading n={9}>Multi-Line Strings</SectionHeading>
   <Para>No more <Code>\n</Code> and concatenation — write the string across lines as-is:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `// before\nconst html = "<div>\\n" + "  <h1>Hello</h1>\\n" + "</div>";\n\n// after\nconst html = \`<div>\n  <h1>Hello</h1>\n</div>\`;` }]} defaultLanguage="javascript" id="tl-multiline" />
  </section>

  {/* 10. HTML generation */}
  <section className="mb-12">
   <SectionHeading n={10}>HTML Generation</SectionHeading>
   <Para>A frontend staple — and the pattern that inspired JSX in React:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const user = { name: "Sachin" };\n\nconst card = \`\n  <div class="card">\n    <h2>\${user.name}</h2>\n  </div>\`;` }]} defaultLanguage="javascript" id="tl-html" />
  </section>

  {/* 11. Loop rendering */}
  <section className="mb-12">
   <SectionHeading n={11}>Loop-Based Rendering</SectionHeading>
   <Para>Combine <Code>map</Code> + <Code>join</Code> to build list markup — a common interview exercise:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const products = ["Laptop", "Phone", "Tablet"];\n\nconst html = \`\n  <ul>\n    \${products.map(p => \`<li>\${p}</li>\`).join("")}\n  </ul>\`;` }]} defaultLanguage="javascript" id="tl-loop" />
   <Output>{`<ul><li>Laptop</li><li>Phone</li><li>Tablet</li></ul>`}</Output>
  </section>

  {/* 12. SQL + logging */}
  <section className="mb-12">
   <SectionHeading n={12}>Backend: Queries &amp; Logging</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `console.log(\`User \${userId} logged in\`); // clean logs\n\nconst query = \`SELECT * FROM users WHERE id = \${userId}\`;` }]} defaultLanguage="javascript" id="tl-sql" />
   <Callout tone="warning" title="Security">Never interpolate raw user input directly into SQL — that&apos;s a <Mark>SQL injection</Mark> risk. Use parameterized queries instead.</Callout>
  </section>

  {/* 13. Tagged templates */}
  <section className="mb-12">
   <SectionHeading n={13}>Tagged Templates (Advanced)</SectionHeading>
   <Para>A function can &quot;tag&quot; a template, receiving the static string parts and the interpolated values separately:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `function tag(strings, ...values) {\n  console.log(strings); // ["Hello ", ""]\n  console.log(values);  // ["Sachin"]\n}\n\ntag\`Hello \${"Sachin"}\`;` }]} defaultLanguage="javascript" id="tl-tagged" />
   <Para>This powers libraries like styled-components, GraphQL (<Code>gql</Code>), and SQL builders.</Para>
  </section>

  {/* 14. Across languages */}
  <section className="mb-12">
   <SectionHeading n={14}>Across Languages</SectionHeading>
   <CodeBlock snippets={[{ language: 'typescript', code: `const name: string = "Sachin";\nconst message: string = \`Hello \${name}\`;` }]} defaultLanguage="typescript" id="tl-ts" />
   <CodeBlock snippets={[{ language: 'java', code: `String name = "Sachin";\nString message = "Hello " + name; // or String.format("Hello %s", name)` }]} defaultLanguage="java" id="tl-java" />
   <Callout tone="info">Java relied on concatenation and <Code>String.format()</Code>; JS template literals stay more readable.</Callout>
  </section>

  {/* 15. Common mistakes */}
  <section className="mb-12">
   <SectionHeading n={15}>Common Mistakes</SectionHeading>
   <Callout tone="warning" title="Quotes instead of backticks"><Code>"Hello {`\${name}`}"</Code> prints the literal text — interpolation only works inside backticks.</Callout>
   <Callout tone="warning" title="Heavy logic inside templates">Long <Code>filter().map().reduce()</Code> chains inside <Code>{`\${ }`}</Code> hurt readability — compute first, interpolate after.</Callout>
   <Callout tone="warning" title="Using them when plain strings suffice">If there&apos;s nothing dynamic, a normal string is fine.</Callout>
  </section>

  {/* 16. Real interview questions */}
  <section className="mb-12">
   <SectionHeading n={16}>Real Interview Questions</SectionHeading>
   <QA q="What are template literals?">
    ES6 strings written with backticks that support interpolation, multi-line text, embedded expressions, and tagged templates.
   </QA>
   <QA q="Difference between concatenation and interpolation?">
    Concatenation glues pieces with <Code>+</Code>; interpolation embeds values inline with <Code>{`\${ }`}</Code> — cleaner and less error-prone.
   </QA>
   <QA q={<>Can you run expressions inside <Code>{`\${ }`}</Code>?</>}>
    Yes — any expression that produces a value: arithmetic, method calls, ternaries, even function calls.
   </QA>
   <QA q="What are tagged templates?">
    A function placed before a template literal that receives the string segments and interpolated values separately, enabling custom processing (styled-components, GraphQL, etc.).
   </QA>
   <QA q="Why are template literals safer/cleaner for multi-line strings?">
    They preserve line breaks literally, removing the need for <Code>\n</Code> and string concatenation.
   </QA>
  </section>

  {/* 17. Mental model */}
  <section className="mb-12">
   <SectionHeading n={17}>Mental Model</SectionHeading>
   <Para>Think of a document template: <Code>Hello {`{NAME}`}, your order {`{ORDER_ID}`} shipped.</Code> JavaScript fills the <Code>{`\${ }`}</Code> placeholders with real values — exactly how template literals work.</Para>
  </section>

  {/* 18 & 19 */}
  <section className="mb-4">
   <SectionHeading n={18}>Interview One-Liner &amp; Takeaway</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4 mb-4">
    <div className="text-xs font-semibold uppercase tracking-wider text-amber-600 dark:text-amber-400 mb-2">Interview One-Liner</div>
    <p className="text-[15px] leading-7 font-medium text-gray-800 dark:text-gray-200">
     Template literals are ES6 backtick strings that support interpolation, multi-line strings, embedded expressions, and tagged templates.
    </p>
   </div>
   <Callout tone="info" title="Most important takeaway">
    Template literals replaced most string concatenation in modern JavaScript. They&apos;re the default choice for dynamic strings — used heavily in frontends, backends, logging, and HTML generation.
   </Callout>
  </section>
 </div>
);

export default JSTemplateLiterals;
